
import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";

// Import our components
import { ClassDetailsCard } from "./ClassDetailsCard";
import { BookingSuccess } from "./BookingSuccess";
import { PersonalDetailsSection } from "./PersonalDetailsSection";
import { GroupBookingSection } from "./GroupBookingSection";
import { GiftCardSection } from "./GiftCardSection";
import { AccountCreationSection } from "./AccountCreationSection";
import { SubscriptionSection } from "./SubscriptionSection";
import { PaymentInformationSection } from "./PaymentInformationSection";
import { useGiftCardVerification } from "./useGiftCardVerification";
import { useBookingForm } from "./hooks/useBookingForm";
import { useAuth } from "@/lib/hooks/auth";
import { useUserSubscriptions } from "./hooks/useUserSubscriptions";

export const BookingFormContainer: React.FC = () => {
  const { user } = useAuth();
  const { userSubscriptions, isLoading: isLoadingSubscriptions } = useUserSubscriptions();
  
  // Use our custom hook for gift card verification
  const { 
    giftCardVerified, 
    verifiedGiftCardId,
    isVerifyingCard, 
    handleVerifyGiftCard,
    resetGiftCardVerification 
  } = useGiftCardVerification();

  // Use our custom form hook
  const {
    form,
    classDetails,
    isClassLoading,
    bookingSuccess,
    createdUserId,
    verificationEmailSent,
    isSubmitting,
    selectedSubscription,
    isGroupBooking,
    groupSize,
    useGiftCard,
    groupNameInputs,
    updateGroupName,
    onSubmit,
    bookingId,
    paymentStatus
  } = useBookingForm(verifiedGiftCardId);

  // Check if the user is logged in - consider both the auth context AND localStorage
  const isUserLoggedIn = !!user || !!localStorage.getItem('userId');
  
  // For debugging
  console.log("BookingFormContainer state:", { 
    isUserLoggedIn, 
    currentUser: user,
    giftCardVerified, 
    verifiedGiftCardId, 
    useGiftCard,
    userSubscriptions,
    isLoadingSubscriptions,
    availableSpots: classDetails?.availableSpots,
    bookingId,
    paymentStatus
  });
  
  if (bookingSuccess) {
    return <BookingSuccess 
      createdUserId={createdUserId}
      verificationEmailSent={verificationEmailSent}
      bookingId={bookingId}
      paymentStatus={paymentStatus}
    />;
  }

  // The book button should be disabled if:
  // 1. Form is submitting
  // 2. Class is loading
  // 3. No class details
  // 4. No available spots
  // 5. Using gift card but it's not verified
  const isBookButtonDisabled = isSubmitting || 
    isClassLoading || 
    !classDetails || 
    classDetails.availableSpots <= 0 || 
    (useGiftCard && !giftCardVerified);

  return (
    <div className="w-full max-w-md mx-auto">
      <ClassDetailsCard classDetails={classDetails} isLoading={isClassLoading} />
      
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <PersonalDetailsSection form={form} />
            
            <GroupBookingSection 
              form={form}
              isGroupBooking={isGroupBooking}
              groupSize={groupSize}
              groupNameInputs={groupNameInputs}
              updateGroupName={updateGroupName}
            />
            
            <GiftCardSection
              form={form}
              useGiftCard={useGiftCard}
              isVerifyingCard={isVerifyingCard}
              giftCardVerified={giftCardVerified}
              onVerifyGiftCard={handleVerifyGiftCard}
              onResetVerification={resetGiftCardVerification}
            />
            
            {/* Subscription Section - Only show if user is logged in and not using a gift card */}
            {isUserLoggedIn && !useGiftCard && (
              <SubscriptionSection 
                form={form}
                userSubscriptions={userSubscriptions} 
                useGiftCard={useGiftCard}
                isLoadingSubscriptions={isLoadingSubscriptions}
              />
            )}
            
            {/* Payment Information Section with conditional display */}
            <PaymentInformationSection 
              useGiftCard={useGiftCard}
              useSubscription={selectedSubscription}
            />
            
            {/* Only show account creation section if user is NOT logged in */}
            {!isUserLoggedIn && (
              <AccountCreationSection 
                form={form}
                createAccount={form.watch("createAccount")}
                useGiftCard={useGiftCard}
              />
            )}
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isBookButtonDisabled}
            >
              {isSubmitting ? "Processing..." : 
               !classDetails ? "Class Not Found" : 
               classDetails.availableSpots <= 0 ? "Class Full" :
               useGiftCard ? "Book with Gift Card" :
               isGroupBooking ? `Book for ${groupSize} people` :
               selectedSubscription && selectedSubscription !== 'pay_individually' ? "Book with Subscription" : 
               "Complete Booking"}
            </Button>
          </form>
        </Form>
      </motion.div>
    </div>
  );
};
